export const API = process.env.REACT_APP_BACKEND_URL;

export const CATEGORY_LABELS = {
  'power-tools': 'Power Tools',
  'hand-tools': 'Hand Tools',
  'lumber': 'Lumber',
  'plumbing': 'Plumbing',
  'electrical': 'Electrical',
  'hvac': 'HVAC',
  'flooring': 'Flooring',
  'drywall': 'Drywall',
  'roofing': 'Roofing',
  'paint-supplies': 'Paint & Supplies',
  'concrete-masonry': 'Concrete & Masonry',
  'hardware-fasteners': 'Hardware & Fasteners',
  'ladders-scaffolding': 'Ladders & Scaffolding',
  'safety-gear': 'Safety Gear',
  'heavy-equipment': 'Heavy Equipment',
  'other': 'Other',
};

export const CONDITION_LABELS = {
  'new': 'New',
  'like-new': 'Like New',
  'used': 'Used',
  'for-parts': 'For Parts',
};

export const CONDITION_COLORS = {
  'new': 'bg-emerald-500',
  'like-new': 'bg-teal-500',
  'used': 'bg-[#D95800]',
  'for-parts': 'bg-neutral-500',
};

export const DELIVERY_LABELS = {
  pickup: 'Local Pickup',
  delivery: 'Local Delivery',
  shipping: 'Shipping',
};

export const photoUrl = (fileId) => `${API}/api/marketplace/photo/${fileId}`;

export const formatPrice = (p) =>
  p === 0 ? 'Free' : `$${Number(p).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const CATEGORY_SEO = {
  'power-tools': {
    h1: 'New & Used Power Tools for Sale',
    intro: 'Buy and sell drills, saws, sanders, grinders, and cordless combo kits from local contractors and homeowners. Pro-grade brands like DeWalt, Milwaukee, and Makita at a fraction of retail — or list your own gear in minutes.',
    faqs: [
      ['Are used power tools worth buying?', 'Yes — contractor-owned tools are often well maintained and sell for 40-70% below retail. Check listing photos, condition badges, and ask the seller questions through in-app messaging before you buy.'],
      ['How do I pay for a power tool safely?', 'Use the Buy Now button to pay securely through Stripe on ConstructHelp, or message the seller to arrange local pickup and pay in person.'],
      ['Can I sell my old power tools here?', 'Absolutely. Listing is free for everyone — add up to 5 photos, set your price, and buyers contact you directly.'],
    ],
  },
  'hand-tools': {
    h1: 'Hand Tools for Sale — New & Used',
    intro: 'Hammers, wrenches, chisels, levels, and complete tool sets from real tradespeople. Quality hand tools last decades — find pro-grade steel at yard-sale prices.',
    faqs: [
      ['What hand tools hold their value best?', 'Forged steel tools from brands like Klein, Estwing, and Stanley last generations. Used prices typically run 50% below retail with little functional difference.'],
      ['Is it free to list hand tools?', 'Yes — anyone can list for free with up to 5 photos per listing.'],
    ],
  },
  'lumber': {
    h1: 'Leftover Lumber & Wood for Sale',
    intro: 'Dimensional lumber, plywood, pressure-treated boards, and hardwood left over from real jobs. Contractors sell surplus lots below big-box prices — great for decks, framing, and DIY projects.',
    faqs: [
      ['Why is leftover lumber cheaper?', 'Contractors over-order to avoid mid-job supply runs. Surplus lots are sold at or below cost to clear storage space — the wood is usually new and unused.'],
      ['How do I transport a lumber lot?', 'Most sellers offer local pickup; some offer delivery. Check the delivery badges on each listing and coordinate through in-app messaging.'],
    ],
  },
  'plumbing': {
    h1: 'Plumbing Supplies & Fixtures for Sale',
    intro: 'Copper pipe, PEX, fittings, water heaters, faucets, and fixtures from plumbers and remodelers. New surplus and gently used stock at real-world prices.',
    faqs: [
      ['Is surplus copper pipe safe to use?', 'Yes — unused copper and PEX from a licensed plumber\'s surplus is identical to store stock. Verify lengths and type (L/M) in the listing description.'],
    ],
  },
  'electrical': {
    h1: 'Electrical Supplies for Sale',
    intro: 'Wire, breakers, panels, conduit, outlets, and lighting from electricians clearing surplus. Romex and THHN by the roll at prices below the big-box stores.',
    faqs: [
      ['Can I buy leftover electrical wire?', 'Yes — partial rolls and full boxes of Romex/THHN are commonly listed by electricians after jobs. Check the gauge and length in the description.'],
    ],
  },
  'hvac': {
    h1: 'HVAC Equipment & Parts for Sale',
    intro: 'Mini-splits, furnaces, condensers, ductwork, and thermostats — new surplus and pulled-but-working units from HVAC pros.',
    faqs: [
      ['Should I buy a used HVAC unit?', 'Working pulls can be a great deal for garages and workshops. Ask the seller for the install date, tonnage, and refrigerant type before buying.'],
    ],
  },
  'flooring': {
    h1: 'Flooring for Sale — Surplus & Overstock',
    intro: 'Hardwood, LVP, tile, and carpet left over from installs. Flooring surplus often sells 50-70% below retail — perfect when you only need a room\'s worth.',
    faqs: [
      ['How much extra flooring should I buy?', 'Add 10% to your square footage for cuts and waste. Surplus lots on the marketplace list total square footage in the description.'],
    ],
  },
  'drywall': {
    h1: 'Drywall & Supplies for Sale',
    intro: 'Sheets, joint compound, corner bead, and tape from finishing crews. Save on materials for your next patch or full room.',
    faqs: [
      ['Can drywall sheets be transported in a pickup?', 'Yes — 4x8 sheets fit flat in a full-size bed. Most sellers offer local pickup; bring a helper, sheets run 50-70 lbs each.'],
    ],
  },
  'roofing': {
    h1: 'Roofing Materials for Sale',
    intro: 'Shingles, underlayment, flashing, and metal panels from roofing crews clearing job surplus. Bundle lots at below-wholesale prices.',
    faqs: [
      ['How many bundles of shingles do I need?', 'Three bundles cover roughly one square (100 sq ft). Surplus listings state bundle counts — message the seller to confirm color/lot match.'],
    ],
  },
  'paint-supplies': {
    h1: 'Paint & Painting Supplies for Sale',
    intro: 'Unopened paint, sprayers, ladders, drop cloths, and brushes from painting contractors. Name-brand paint at clearance prices.',
    faqs: [
      ['Is leftover paint still good?', 'Unopened cans last 10+ years. For opened cans, ask the seller when it was opened and how it was stored.'],
    ],
  },
  'concrete-masonry': {
    h1: 'Concrete & Masonry Supplies for Sale',
    intro: 'Bagged concrete, pavers, blocks, brick, rebar, and masonry tools. Heavy materials sold locally — skip the delivery fees.',
    faqs: [
      ['Does bagged concrete expire?', 'Kept dry, bagged mix lasts about a year. Check listing photos for intact packaging and ask about storage.'],
    ],
  },
  'hardware-fasteners': {
    h1: 'Hardware & Fasteners for Sale',
    intro: 'Screws, nails, bolts, anchors, hinges, and bulk fastener lots. Contractors sell full boxes at a fraction of per-piece retail.',
    faqs: [
      ['Why buy fasteners in bulk lots?', 'Per-piece cost drops dramatically — a surplus box of deck screws often costs less than a small retail pack.'],
    ],
  },
  'ladders-scaffolding': {
    h1: 'Ladders & Scaffolding for Sale',
    intro: 'Extension ladders, step ladders, scaffolding frames, and planks from working crews. Inspect in person at pickup — most listings are local.',
    faqs: [
      ['What should I check on a used ladder?', 'Look for bent rungs, cracked rails, and worn feet. Ask the seller for close-up photos and the duty rating (Type I/IA for pro use).'],
    ],
  },
  'safety-gear': {
    h1: 'Safety Gear & PPE for Sale',
    intro: 'Hard hats, harnesses, goggles, gloves, and hi-vis clothing. New surplus safety equipment from crews and suppliers.',
    faqs: [
      ['Can fall-protection harnesses be bought used?', 'Only buy unused harnesses with tags — fall protection should never be purchased after a deployment. Listings marked NEW are surplus stock.'],
    ],
  },
  'heavy-equipment': {
    h1: 'Heavy Equipment for Sale',
    intro: 'Compactors, mixers, generators, trailers, and small machinery from contractors upgrading their fleet. Inspect before you buy with local pickup.',
    faqs: [
      ['How do I inspect used equipment before buying?', 'Message the seller to arrange an in-person start-up test at pickup. Check hours, service history, and look for leaks.'],
    ],
  },
  'other': {
    h1: 'Construction Tools & Materials — Everything Else',
    intro: 'Job-site storage, work benches, shop vacs, and everything that doesn\'t fit a category. Browse the misc deals from local pros.',
    faqs: [
      ['What can I list in the Other category?', 'Any construction-related item that doesn\'t fit our main categories — job boxes, heaters, work lights, trailers, and more.'],
    ],
  },
};
